import { cn } from "@/lib/utils";
import { FormProvider, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import Logo from "@/components/atoms/logo";
import { Avatar, AvatarImage } from "@/components/ui/avatar";
import { ConfettiSideCannons } from "@/components/atoms/confetti";
import {
  CollectionForm,
  testimonialFormSchema,
  TestimonialFormTypes,
} from "../utils/schema";
import { Toaster } from "@/components/ui/toaster";
import { getTestimonialCollectionStepConfig } from "../utils/review-screen-mapper";
import { getCollectionFormStyles } from "../utils/extract-review-form-styles";
import { ScreenId } from "../types";
import { getImage } from "../utils/helpers";
import { useCreateTestimonial } from "../api/mutations";
import { useToast } from "@/hooks/use-toast";
import { validateReviewForm } from "../utils/validation";

interface Props {
  data: CollectionForm;
  step: ScreenId;
  onStepChange: (stepId: ScreenId) => void;
}

export function TestimonialCollection({ data, step, onStepChange }: Props) {
  const toaster = useToast();
  const cssVariants = getCollectionFormStyles(data);
  const { mutateAsync, isPending } = useCreateTestimonial();
  const config = getTestimonialCollectionStepConfig(data, onStepChange);

  const form = useForm<TestimonialFormTypes>({
    resolver: zodResolver(testimonialFormSchema),
    defaultValues: {
      published: data.settings.shouldAutoPopulateWall || false,
      projectId: data.projectId,
      date: new Date(),
    },
  });

  const onSubmit = async (values: TestimonialFormTypes) => {
    const formData = new FormData();

    Object.entries(values).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        if (key === "customer") {
          formData.append(key, JSON.stringify(value));
        } else if (key === "media" && Array.isArray(value)) {
          value.forEach((file) => formData.append("media", file));
        } else {
          formData.append(key, value as string | Blob);
        }
      }
    });

    if (values.customer.avatar instanceof File) {
      formData.append("avatar", values.customer.avatar);
    }

    const isValid = validateReviewForm(data, values, toaster, formData);

    if (!isValid) return;

    await mutateAsync(formData);

    if (!isPending) {
      onStepChange("thankYou");
      toaster.toast({ description: data.thankYouPage.title });
    }
  };

  return (
    <ConfettiSideCannons sprayWhen={step === "thankYou"}>
      <link
        key={data.branding.style.font}
        rel="stylesheet"
        href={cssVariants["font"]}
      />

      <FormProvider {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)}>
          <div className="testify-min-h-screen testify-w-full testify-flex testify-flex-col testify-overflow-y-auto">
            <main
              style={{
                background: cssVariants["background"],
                fontFamily: cssVariants["fontFamily"],
                color: cssVariants["textColor"],
              }}
              className="testify-flex-1 testify-w-full testify-flex testify-items-center testify-justify-center testify-overflow-y-auto"
            >
              <div className="testify-container testify-mx-auto testify-py-8 testify-px-4">
                <div className="testify-flex testify-justify-center testify-w-full testify-mb-6">
                  <Avatar
                    className={cn(
                      "testify-h-16 testify-w-16 testify-rounded-none",
                      {
                        "testify-rounded-full":
                          !data.branding.style.isSquareLogo,
                      },
                    )}
                  >
                    <AvatarImage
                      className={cn(
                        "testify-h-full testify-w-full testify-object-cover",
                        {
                          "testify-rounded-none":
                            data.branding.style.isSquareLogo,
                          "testify-rounded-full":
                            !data.branding.style.isSquareLogo,
                        },
                      )}
                      src={getImage(data.branding.logo, "/default-logo.png")}
                    />
                  </Avatar>
                </div>
                <div className="testify-max-w-lg testify-mx-auto">
                  {/* Review Collection Form Screen */}
                  {config[step]}

                  {/* Powered By Section */}
                  {!data.branding.removeTestifyBranding && (
                    <div className="testify-flex testify-items-center testify-justify-center testify-space-x-2 testify-mt-6 testify-text-gray-500">
                      <span className="testify-text-sm">POWERED BY</span>
                      <Logo />
                    </div>
                  )}
                </div>
              </div>
            </main>
          </div>
          <Toaster />
        </form>
      </FormProvider>
    </ConfettiSideCannons>
  );
}
